<template>
  <v-container>
    <v-layout row>
      <v-flex>
        <v-card-text>
          <v-container>
            <h2>Rehearsal Recordings — IABF {{ adminFestivalYear }}</h2>
            <p class="muted">
              <template v-if="scheduleIsHardcoded">
                Paste a recording URL (Zoom, YouTube, Dropbox, anything publicly viewable) and click <strong>Save</strong>.
                Dancers see it on Past rehearsals as soon as a URL is set.
              </template>
              <template v-else>
                {{ adminFestivalYear }} has its own rehearsal dates and pieces — not the 2026 corps schedule.
                Add sessions below, then paste recording URLs. Dancers on {{ adminFestivalYear }} only see this year’s list.
              </template>
            </p>

            <div v-if="!scheduleIsHardcoded" class="add-session">
              <strong>Add a session</strong>
              <div class="add-session-row">
                <label>
                  Date
                  <input v-model="draft.date" type="date">
                </label>
                <label class="grow">
                  Piece
                  <input v-model="draft.pieceName" type="text" placeholder="e.g. opening ballet">
                </label>
                <label>
                  Start
                  <input v-model="draft.start" type="time">
                </label>
                <label>
                  End
                  <input v-model="draft.end" type="time">
                </label>
                <v-btn
                  small
                  color="primary"
                  dark
                  :disabled="!canAddSession || adding"
                  :loading="adding"
                  @click="addSession"
                >
                  Add
                </v-btn>
              </div>
            </div>

            <div v-if="loading" class="muted">Loading…</div>

            <p v-else-if="!rows.length" class="muted empty-note">
              No rehearsal sessions for {{ adminFestivalYear }} yet.
            </p>

            <table v-else class="rehearsals-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Piece</th>
                  <th>Recording URL</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in rows" :key="row.key" :class="{ 'is-past': row.isPast }">
                  <td>{{ row.dateLabel }}</td>
                  <td>{{ row.pieceName }}</td>
                  <td>
                    <input
                      type="text"
                      v-model="row.url"
                      class="url-input"
                      placeholder="https://…"
                    />
                  </td>
                  <td class="row-actions">
                    <v-btn x-small color="primary" :loading="saving === row.key" :disabled="row.url === (savedRecordings[row.key] || '')" @click="onSave(idx)">Save</v-btn>
                    <v-btn x-small text v-if="row.url" @click="row.url = ''">Clear</v-btn>
                    <button
                      v-if="!scheduleIsHardcoded"
                      type="button"
                      class="remove-btn"
                      title="Remove session"
                      :disabled="removing === row.key"
                      @click="removeSession(row)"
                    >Remove</button>
                    <span v-if="savedFlash === row.key" class="saved-flash">✓ Saved</span>
                  </td>
                </tr>
              </tbody>
            </table>

          </v-container>
        </v-card-text>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import { db } from '@/firebase'
import iabfAdminMixin from '@/utils/iabf-admin-mixin'
import {
  rehearsalsForYear,
  yearHasHardcodedRehearsals,
  pieceKeyFromName,
  normalizeRehearsalDays
} from '@/utils/iabf-rehearsals'

function formatDate (iso) {
  const d = new Date(iso + 'T12:00:00')
  return d.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })
}

function recordingKey (date, pieceKey) {
  return `${date}:${pieceKey}`
}

export default {
  mixins: [iabfAdminMixin],
  data () {
    return {
      loading: true,
      savedRecordings: {},
      storedSessions: [],
      rows: [],
      saving: null,
      savedFlash: null,
      adding: false,
      removing: null,
      draft: {
        date: '',
        pieceName: '',
        start: '11:00',
        end: '12:00'
      }
    }
  },
  computed: {
    scheduleIsHardcoded () {
      return yearHasHardcodedRehearsals(this.adminFestivalYear) &&
        !(this.storedSessions && this.storedSessions.length)
    },
    canAddSession () {
      return !!(this.draft.date && String(this.draft.pieceName || '').trim())
    }
  },
  async created () {
    await this.load()
  },
  methods: {
    scheduleDays () {
      return rehearsalsForYear(this.adminFestivalYear, this.storedSessions)
    },
    buildRows () {
      const today = new Date().toISOString().slice(0, 10)
      const rows = []
      for (const day of this.scheduleDays()) {
        for (const b of day.blocks) {
          const key = recordingKey(day.date, b.pieceKey)
          rows.push({
            key,
            date: day.date,
            dateLabel: formatDate(day.date),
            pieceKey: b.pieceKey,
            pieceName: b.pieceName,
            start: b.start,
            end: b.end,
            url: this.savedRecordings[key] || '',
            isPast: day.date < today
          })
        }
      }
      rows.sort((a, b) => {
        if (a.isPast !== b.isPast) return a.isPast ? -1 : 1
        if (a.date !== b.date) {
          return a.isPast ? (a.date < b.date ? 1 : -1) : (a.date < b.date ? -1 : 1)
        }
        return String(a.start || '').localeCompare(String(b.start || ''))
      })
      this.rows = rows
    },
    async load () {
      this.loading = true
      const doc = await db.collection(this.IABF.config).doc('rehearsal-recordings').get()
      const data = doc.exists ? (doc.data() || {}) : {}
      this.savedRecordings = data.recordings || {}
      this.storedSessions = normalizeRehearsalDays(data.sessions)
      this.buildRows()
      this.loading = false
    },
    async writeDoc (patch) {
      const recordings = Object.prototype.hasOwnProperty.call(patch, 'recordings')
        ? patch.recordings
        : this.savedRecordings
      const sessions = Object.prototype.hasOwnProperty.call(patch, 'sessions')
        ? patch.sessions
        : this.storedSessions
      await db.collection(this.IABF.config).doc('rehearsal-recordings').set({
        recordings,
        sessions
      }, { merge: true })
      this.savedRecordings = recordings
      this.storedSessions = normalizeRehearsalDays(sessions)
    },
    async onSave (idx) {
      const row = this.rows[idx]
      if (!row) return
      this.saving = row.key
      try {
        const next = { ...this.savedRecordings }
        if (row.url.trim()) next[row.key] = row.url.trim()
        else delete next[row.key]
        await this.writeDoc({ recordings: next })
        this.savedFlash = row.key
        setTimeout(() => { if (this.savedFlash === row.key) this.savedFlash = null }, 1800)
      } catch (e) {
        console.error('Save failed:', e)
        // eslint-disable-next-line no-alert
        window.alert('Save failed: ' + (e.message || ''))
      }
      this.saving = null
    },
    async addSession () {
      const date = String(this.draft.date || '').trim()
      const pieceName = String(this.draft.pieceName || '').trim()
      if (!date || !pieceName) return
      const pieceKey = pieceKeyFromName(pieceName)
      const start = String(this.draft.start || '11:00').trim()
      const end = String(this.draft.end || '12:00').trim()
      const days = this.scheduleDays().map(d => ({
        date: d.date,
        blocks: d.blocks.map(b => ({ ...b }))
      }))
      let day = days.find(d => d.date === date)
      if (!day) {
        day = { date, blocks: [] }
        days.push(day)
      }
      let key = pieceKey
      const used = new Set(day.blocks.map(b => b.pieceKey))
      if (used.has(key)) {
        key = pieceKey + '-' + start.replace(':', '')
      }
      day.blocks.push({
        pieceKey: key,
        pieceName,
        start,
        end,
        splits: []
      })
      days.sort((a, b) => a.date.localeCompare(b.date))
      this.adding = true
      try {
        await this.writeDoc({ sessions: days })
        this.draft.pieceName = ''
        this.buildRows()
      } catch (e) {
        console.error('Add session failed:', e)
        // eslint-disable-next-line no-alert
        window.alert('Could not add session: ' + (e.message || ''))
      }
      this.adding = false
    },
    async removeSession (row) {
      if (!row) return
      // eslint-disable-next-line no-alert
      if (!window.confirm('Remove this rehearsal session from ' + this.adminFestivalYear + '?')) {
        return
      }
      this.removing = row.key
      try {
        const days = this.scheduleDays()
          .map(d => ({
            date: d.date,
            blocks: d.blocks.filter(b => !(d.date === row.date && b.pieceKey === row.pieceKey))
          }))
          .filter(d => d.blocks.length)
        const nextRecordings = { ...this.savedRecordings }
        delete nextRecordings[row.key]
        await this.writeDoc({ sessions: days, recordings: nextRecordings })
        this.buildRows()
      } catch (e) {
        console.error('Remove session failed:', e)
        // eslint-disable-next-line no-alert
        window.alert('Could not remove session: ' + (e.message || ''))
      }
      this.removing = null
    }
  }
}
</script>

<style scoped>
h2 { font-family: Quattrocento, serif; font-weight: 400; font-size: 28px; margin: 0; }
.muted { color: #888; font-style: italic; }
.empty-note { margin-top: 16px; }
.add-session {
  margin: 16px 0 8px;
  padding: 14px 16px;
  background: #faf9f7;
  border: 1px solid #eee;
  border-radius: 8px;
}
.add-session strong { display: block; margin-bottom: 10px; color: #1a1a1a; font-style: normal; }
.add-session-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: flex-end;
}
.add-session-row label {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #888;
}
.add-session-row label.grow { flex: 1; min-width: 12rem; }
.add-session-row input {
  padding: 6px 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
  color: #1a1a1a;
}
.rehearsals-table { width: 100%; border-collapse: collapse; font-size: 14px; margin-top: 12px; }
.rehearsals-table th { text-align: left; padding: 8px; font-size: 11px; text-transform: uppercase; color: #999; border-bottom: 1px solid #eee; }
.rehearsals-table td { padding: 10px 8px; border-bottom: 1px solid #f3f1ed; vertical-align: middle; }
.rehearsals-table tr.is-past td { background: #fafaf8; }
.url-input { width: 100%; padding: 6px 10px; border: 1px solid #ccc; border-radius: 4px; font-size: 13px; font-family: 'SF Mono', Menlo, Consolas, monospace; }
.row-actions { white-space: nowrap; }
.remove-btn {
  margin-left: 6px;
  border: none;
  background: none;
  color: #c0392b;
  cursor: pointer;
  font-size: 12px;
  text-decoration: underline;
}
.saved-flash { color: #2e7d32; font-weight: 600; margin-left: 8px; }
</style>
